import { useEffect, useState } from 'react';
import type { RejectEmailRecipient } from '@hcc/types';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';

/**
 * Request-changes dialog shared by the review queue, the dedicated review
 * screen, the checks push-back, and the person tasks panel. The inductee
 * is emailed the reason, so it's required. Most haven't started yet when
 * a task is sent back, so the email defaults to their personal address;
 * the dropdown lets HR force the work one. Kept presentational: the
 * caller owns the mutation and busy state.
 */
export function RejectTaskModal({
  open,
  taskTitle,
  workEmail,
  personalEmail,
  busy,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  taskTitle: string;
  workEmail: string;
  personalEmail: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string, recipient: RejectEmailRecipient) => void;
}) {
  const [reason, setReason] = useState('');
  const [recipient, setRecipient] = useState<RejectEmailRecipient>('personal');

  // Re-derive the defaults each time the dialog opens for a (possibly
  // different) person rather than carrying the previous choice over.
  // The recipient also re-derives if personalEmail arrives after open
  // (callers may lazy-fetch it), without wiping a typed reason.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  useEffect(() => {
    if (open) setRecipient(personalEmail ? 'personal' : 'work');
  }, [open, personalEmail]);

  return (
    <ModalShell open={open} onClose={onCancel} titleId="reject-task-title" busy={busy}>
      <form
        className="p-5"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(reason.trim(), recipient);
        }}
      >
        <h2 id="reject-task-title" className="text-base font-semibold">
          Request changes
        </h2>
        <p className="mt-1 text-xs text-fg-muted">
          The inductee will be emailed this reason. HR may also follow up directly.
        </p>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-fg-muted">Email the reason to</span>
          <select
            value={recipient}
            onChange={(e) => setRecipient(e.target.value as RejectEmailRecipient)}
            disabled={!personalEmail}
            className="mt-1 block w-full rounded-md border border-line-strong/20 bg-surface px-2 py-1.5 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 disabled:opacity-70"
          >
            {personalEmail ? (
              <option value="personal">Personal — {personalEmail}</option>
            ) : null}
            <option value="work">Work — {workEmail}</option>
          </select>
          {!personalEmail ? (
            <span className="mt-1 block text-xs text-fg-dim">
              No personal email on record, so this goes to the work address.
            </span>
          ) : null}
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-fg-muted">Why "{taskTitle}" needs more</span>
          <textarea
            required
            minLength={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="mt-1 block w-full rounded-md border border-line-strong/20 px-2 py-1.5 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="md" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" loading={busy}>
            Send rejection
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
